import { useEffect, useRef, useState } from "react";
import { Newspaper, Wallet, LayoutDashboard, Trophy, Gift, BarChart3, Coffee, Building2, LogIn, HelpCircle, Flag, Clapperboard, Share2, Sparkles, Users as UsersIcon, Megaphone, Radio, Shield, Handshake } from "lucide-react";
import { openShareSheet } from "@/components/viral/ShareSheetHost";
import { Link } from "@tanstack/react-router";
import { useMode, isBettingDemoUrl } from "@/lib/mode";
import { trackEvent } from "@/lib/audit";
import { LanguageDropdown } from "@/components/i18n/LanguageDropdown";
import { HelpButton } from "@/components/layout/HelpButton";
import { PointsBadge } from "@/components/gamification/PointsBadge";
import { PredictionReputationBadge } from "@/components/profile/PredictionReputationBadge";
import { StreakBadge } from "@/components/layout/StreakBadge";
import { useLanguage } from "@/hooks/useLanguage";
import { openAdvancedStats } from "@/lib/advancedStats";
import { MatchClock } from "@/components/match/MatchClock";
import { OfficialMatchHeader } from "@/components/match/OfficialMatchHeader";
import { ProfileMenu } from "@/components/layout/ProfileMenu";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { AudioSyncButton } from "@/components/sync/AudioSyncButton";
import { tenantStore } from "@/lib/tenants/tenantStore";
import { useAuth } from "@/hooks/useAuth";
import { useMounted } from "@/hooks/useMounted";
import { PREVIEW_MODE } from "@/lib/previewMode";
import type { MatchSnapshot } from "@/lib/matches";

interface HeaderProps {
  scoreMex: number;
  scoreArg: number;
  minute: number;
  onLogoClick: () => void;
  onAudioSync?: (offsetSeconds: number) => void;
  onHelpClick?: () => void;
  multiMatchEnabled?: boolean;
  onToggleMultiMatch?: () => void;
  points?: number;
  onOpenRewards?: () => void;
  homeCode?: string;
  awayCode?: string;
  homeFlag?: string;
  awayFlag?: string;
  matchStatus?: MatchSnapshot["status"];
  onOpenHalftime?: () => void;
  /**
   * True when the active match is synthetic/demo/replay. Swaps the LIVE
   * badge for a DEMO/REPLAY pill and switches the TV sync surface into a
   * non-error "Modo demo activo" state.
   */
  isDemo?: boolean;
  /**
   * Real Data Phase 5 — when set, the scoreboard row uses the official
   * API-Football header (score/minute/status/kickoff) via
   * `useOfficialMatchHeader`. When absent, the legacy simulated row is
   * kept (already accompanied by the SIMULATED pill so it never renders
   * as official football data).
   */
  providerFixtureId?: string | null;
}

export function Header({ scoreMex, scoreArg, minute, onLogoClick, onAudioSync, onHelpClick, multiMatchEnabled, onToggleMultiMatch, points, onOpenRewards, homeCode = "ESP", awayCode = "ARG", homeFlag = "🇪🇸", awayFlag = "🇦🇷", matchStatus = "LIVE", onOpenHalftime, isDemo = false, providerFixtureId = null }: HeaderProps) {
  const [mode, setMode] = useMode();
  const [showSwitch, setShowSwitch] = useState(false);
  const { t, lang } = useLanguage();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const mounted = useMounted();
  const authReady = mounted && isAuthenticated;

  useEffect(() => {
    if (isBettingDemoUrl()) {
      setShowSwitch(true);
      if (mode !== "betting") {
        setMode("betting");
        trackEvent("edition_switch", { to: "betting", source: "url_param" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMode = () => {
    const next = mode === "media" ? "betting" : "media";
    setMode(next);
    trackEvent("edition_switch", { to: next, source: "manual" });
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/60 glass safe-top">
      <HeaderRows
        scoreMex={scoreMex}
        scoreArg={scoreArg}
        minute={minute}
        homeCode={homeCode}
        awayCode={awayCode}
        homeFlag={homeFlag}
        awayFlag={awayFlag}
        matchStatus={matchStatus}
        onLogoClick={onLogoClick}
        onAudioSync={onAudioSync}
        onHelpClick={onHelpClick}
        multiMatchEnabled={multiMatchEnabled}
        onToggleMultiMatch={onToggleMultiMatch}
        points={points}
        onOpenRewards={onOpenRewards}
        onOpenHalftime={onOpenHalftime}
        isDemo={isDemo}
        providerFixtureId={providerFixtureId}
        showSwitch={showSwitch}
        mode={mode}
        toggleMode={toggleMode}
        t={t}
        lang={lang}
        isAuthenticated={isAuthenticated}
        authLoading={authLoading}
      />
    </header>
  );
}

interface HeaderRowsProps extends HeaderProps {
  showSwitch: boolean;
  mode: "media" | "betting";
  toggleMode: () => void;
  t: (k: string, p?: Record<string, string | number>) => string;
  lang: "ES" | "EN" | "PT";
  isAuthenticated: boolean;
  authLoading: boolean;
}

function HeaderRows({
  scoreMex,
  scoreArg,
  minute,
  homeCode = "ESP",
  awayCode = "ARG",
  homeFlag = "🇪🇸",
  awayFlag = "🇦🇷",
  matchStatus = "LIVE",
  onLogoClick,
  onAudioSync,
  onHelpClick,
  multiMatchEnabled,
  onToggleMultiMatch,
  points,
  onOpenRewards,
  onOpenHalftime,
  isDemo = false,
  providerFixtureId = null,
  showSwitch,
  mode,
  toggleMode,
  t,
  lang,
  isAuthenticated,
  authLoading,
}: HeaderRowsProps) {
  const tracked = useRef(false);
  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    void trackEvent("scoreboard_overlap_fix_applied", { layout: "two_row_responsive" });
  }, []);
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !tracked.current) {
      tracked.current = true;
      void trackEvent("public_signup_cta_viewed", { surface: "header", mode: "public" });
    }
  }, [authLoading, isAuthenticated]);
  const { isSuperAdmin } = useSuperAdmin();
  const mounted = useMounted();
  const authReady = mounted && isAuthenticated;
  useEffect(() => {
    if (isSuperAdmin) {
      void trackEvent("admin_menu_visible", { surface: "header" });
    }
  }, [isSuperAdmin]);
  const impersonatedTenantId =
    typeof window !== "undefined" ? tenantStore.currentImpersonation() : null;
  // Partner Portal visible to super_admin (always — they can preview) and to any tenant_owner/tenant_admin session (modeled by impersonation in this build).
  const showPartnerPortal = isSuperAdmin || Boolean(impersonatedTenantId);
  useEffect(() => {
    if (showPartnerPortal) {
      void trackEvent("partner_portal_visible", {
        surface: "header",
        impersonating: Boolean(impersonatedTenantId),
      });
    }
  }, [showPartnerPortal, impersonatedTenantId]);
  // PR-12.1: never link to a tenant-less portal; default to Univision demo.
  const partnerPortalSearch = {
    tenant: impersonatedTenantId ?? "tenant_univision",
  };
  // PR-23 — every visitor sees a "Partner Portal" entry that lands on the
  // public corporate page; super_admin / impersonated sessions keep the
  // direct deep-link into the tenant portal.
  useEffect(() => {
    void trackEvent("partner_portal_link_visible", { surface: "header", authenticated: isAuthenticated });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-1.5 px-2 py-2 sm:px-4 sm:py-2.5 md:px-6">
      {/* Row 1: Logo · Scoreboard · Primary actions */}
      <div className="flex items-center justify-between gap-3 min-w-0">
        {/* Logo */}
        <button
          onClick={onLogoClick}
          aria-label="VerseSport™ logo"
          className="group flex shrink-0 items-center gap-2 outline-none"
        >
          <div className="relative h-8 w-8 sm:h-9 sm:w-9 rounded-xl bg-gradient-to-br from-[oklch(0.58_0.24_27)] to-[oklch(0.7_0.22_15)] glow-red transition-transform group-hover:scale-110">
            <div className="absolute inset-[3px] rounded-[9px] bg-background flex items-center justify-center text-[10px] font-black tracking-tighter">
              SS
            </div>
          </div>
          <div className="hidden md:block text-left leading-tight">
            <div className="text-sm font-bold tracking-tight">VerseSport</div>
            <div className="text-[10px] font-semibold text-gradient-red">2026 · WORLD CUP</div>
          </div>
        </button>

        {/* Live Score — official when a providerFixtureId is provided.
            Real Data Phase 5: OfficialMatchHeader consumes API-Football via
            unifiedSports. Legacy simulated row remains as fallback only,
            paired with the SIMULATED pill so it never poses as official
            football data. */}
        {providerFixtureId && !isDemo ? (
          <OfficialMatchHeader
            fixtureId={providerFixtureId}
            fallbackHomeCode={homeCode}
            fallbackAwayCode={awayCode}
            fallbackHomeFlag={homeFlag}
            fallbackAwayFlag={awayFlag}
          />
        ) : (
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-3 rounded-2xl glass px-2 sm:px-4 py-1 sm:py-1.5">
          {isDemo ? (
            <div
              className="flex items-center gap-1.5 rounded-full px-1.5 py-0.5"
              style={{ background: "oklch(0.78 0.18 85)" }}
              aria-label={lang === "EN" ? "Simulated match — not a live broadcast" : lang === "PT" ? "Partida simulada — não é transmissão ao vivo" : "Partido simulado — no es transmisión en vivo"}
              title={lang === "EN" ? "Simulated match · Not a live broadcast" : lang === "PT" ? "Partida simulada · Não é transmissão ao vivo" : "Partido simulado · No es transmisión en vivo"}
            >
              <span className="text-[10px] font-black tracking-wider" style={{ color: "oklch(0.18 0.02 80)" }}>
                {lang === "EN" ? "SIMULATED" : lang === "PT" ? "SIMULADA" : "SIMULADO"}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 rounded-full bg-primary px-1.5 py-0.5">
              <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground animate-pulse-dot" />
              <span className="text-[10px] font-black tracking-wider text-primary-foreground">LIVE</span>
            </div>
          )}
          <div className="hidden sm:block h-5 w-px bg-border" />
          <div className="flex items-center gap-1.5 sm:gap-3 text-sm sm:text-base font-bold tabular-nums">
            <span className="flex items-center gap-1">
              <span aria-hidden>{homeFlag}</span>
              <span className="hidden sm:inline">{homeCode}</span>
            </span>
            <span className="text-base sm:text-2xl font-black tabular-nums tracking-tighter">
              {scoreMex} <span className="text-muted-foreground/60">-</span> {scoreArg}
            </span>
            <span className="flex items-center gap-1">
              <span className="hidden sm:inline">{awayCode}</span>
              <span aria-hidden>{awayFlag}</span>
            </span>
          </div>
          <div className="hidden sm:block h-5 w-px bg-border" />
          <MatchClock
            minute={minute}
            status={matchStatus}
            variant="compact"
            className="min-w-[44px]"
          />
        </div>
        )}

        {/* Primary actions (right-aligned). Secondary controls move to row 2 below md.
            PR-37 — increased gap + minimum 40px hit targets to prevent accidental taps.
            PUBLIC_V1 — row is horizontally scrollable on mobile so the
            language dropdown is always reachable even when many badges /
            actions are present. */}
        <div className="flex shrink items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide max-w-[60%] sm:max-w-none [&_button]:min-h-[40px] [&_a]:min-h-[40px]">
          {typeof points === "number" && onOpenRewards && authReady && (
            <PointsBadge points={points} onClick={onOpenRewards} />
          )}
          {authReady && (
            <StreakBadge onClick={onOpenRewards} />
          )}
          {authReady && (
            <div className="hidden sm:flex">
              <PredictionReputationBadge compact />
            </div>
          )}
          <div className="flex items-center gap-1">
            <LanguageDropdown />
          </div>
          {onHelpClick ? (
            <HelpButton onClick={onHelpClick} />
          ) : (
            <Link
              to="/ayuda"
              aria-label="Ayuda"
              title="Centro de ayuda"
              onClick={() => void trackEvent("help_opened", { surface: "header_link" })}
              className="flex h-9 w-9 items-center justify-center rounded-full glass transition hover:bg-accent"
            >
              <HelpCircle className="h-4 w-4" />
            </Link>
          )}
          <button
            type="button"
            aria-label="Compartir VerseSport"
            title="Compartir"
            onClick={() => {
              void trackEvent("global_share_clicked", { surface: "header" });
              openShareSheet();
            }}
            className="flex h-9 w-9 items-center justify-center rounded-full glass transition hover:bg-accent"
          >
            <Share2 className="h-4 w-4 text-primary" />
          </button>
          {!authReady && !PREVIEW_MODE && (
            <>
              <Link
                to="/login"
                search={{ from: undefined }}
                aria-label="Login"
                title="Login"
                onClick={() => void trackEvent("public_login_cta_clicked", { surface: "header_button" })}
                className="hidden md:flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-bold glass hover:bg-accent"
              >
                <LogIn className="h-3.5 w-3.5" />
                <span>{t("btn_login")}</span>
              </Link>
              <Link
                to="/registro"
                aria-label="Sign Up Free"
                title="Sign Up Free"
                onClick={() => void trackEvent("public_signup_cta_clicked", { surface: "header_button" })}
                className="hidden md:flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-bold bg-[oklch(0.58_0.24_27)] text-white shadow hover:bg-[oklch(0.52_0.24_27)]"
              >
                <span>{t("btn_signup_free")}</span>
              </Link>
            </>
          )}
          {isSuperAdmin && (
            <Link
              to="/admin"
              aria-label="Admin dashboard"
              title="Admin"
              onClick={() => void trackEvent("admin_menu_visible", { surface: "header_button" })}
              className="hidden md:flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-bold glass hover:bg-accent"
            >
              <LayoutDashboard className="h-3.5 w-3.5 text-[oklch(0.78_0.18_70)]" />
              <span>Admin</span>
            </Link>
          )}
          {showPartnerPortal && (
            <Link
              to="/tenant-portal"
              search={partnerPortalSearch}
              aria-label="Partner Portal"
              title="Partner Portal"
              onClick={() =>
                void trackEvent("partner_portal_opened", {
                  surface: "header_button",
                  impersonating: Boolean(impersonatedTenantId),
                })
              }
              className="hidden lg:flex shrink-0 items-center justify-center rounded-lg p-1.5 text-muted-foreground/70 hover:bg-accent hover:text-foreground"
            >
              <Building2 className="h-3.5 w-3.5" />
              <span className="sr-only">Partner Portal</span>
            </Link>
          )}
          {authReady && <ProfileMenu onOpenRewards={onOpenRewards} />}
        </div>
      </div>

      {/* Row 2: Secondary controls — wraps cleanly, scrollable on tiny screens.
          PR-37 — wider gap + 40px min-height hit targets. */}
      <div className="flex items-center gap-3 sm:gap-4 overflow-x-auto scrollbar-hide -mx-1 px-1 pr-6 [&>*]:shrink-0 [&>button]:min-h-[44px] [&>a]:min-h-[44px]">
        {/* WORLD-CUP-FINAL-ACTIVATION-P0 — TV Sync re-exposed in header row 2.
            Uses the shared globalTvOffsetBus, so SpoilerSyncSlider in the
            pitch zone keeps full parity (single source of truth, no duplicate
            state). The button is a low-risk preset launcher; full manual
            calibration still lives in the in-pitch slider. */}
        <AudioSyncButton onSynced={onAudioSync} isDemo={isDemo} />
        {/* PR-22 — Rewards dedup: row-1 PointsBadge already opens rewards
            for authenticated users; only render this fallback when the badge
            is not present (anonymous session or no points yet). */}
        {onOpenRewards && !(typeof points === "number" && isAuthenticated) && (
          <button
            onClick={() => {
              onOpenRewards();
              void trackEvent("rewards_navigation_deduplicated", { surface: "header_row2", action: "fallback" });
            }}
            aria-label={t("rewards")}
            title={t("rewards_catalog")}
            className="flex h-8 items-center gap-1.5 rounded-full glass px-2 transition hover:bg-accent"
          >
            <Gift className="h-3.5 w-3.5 text-[oklch(0.85_0.18_85)]" />
            <span className="hidden sm:inline text-[11px] font-bold">{t("rewards")}</span>
          </button>
        )}
          <button
            onClick={openAdvancedStats}
            aria-label={t("adv_stats_title")}
            title={t("adv_stats_title")}
            className="flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-bold glass hover:bg-accent"
          >
            <BarChart3 className="h-3.5 w-3.5 text-primary" />
            <span className="hidden sm:inline">{t("adv_stats_btn")}</span>
          </button>
          {onOpenHalftime && (
            <button
              onClick={() => {
                onOpenHalftime();
                trackEvent("mobile_halftime_hub_opened", { surface: "header" });
              }}
              aria-label={t("halftime_hub")}
              title={t("halftime_hub")}
              className="flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-bold glass hover:bg-accent"
            >
              <Coffee className="h-3.5 w-3.5 text-[oklch(0.82_0.24_145)]" />
              <span className="hidden sm:inline">{t("halftime_hub")}</span>
            </button>
          )}
          {onToggleMultiMatch && (
            <button
              onClick={() => {
                onToggleMultiMatch();
                trackEvent("mobile_match_selector_used", { enabled: !multiMatchEnabled });
              }}
              aria-pressed={!!multiMatchEnabled}
              aria-label={t("multi_match")}
              title={multiMatchEnabled ? t("multi_match_on") : t("multi_match_off")}
              className={`flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-bold transition ${
                multiMatchEnabled
                  ? "bg-primary/15 text-primary border border-primary/40"
                  : "glass text-muted-foreground hover:bg-accent border border-transparent"
              }`}
            >
              <Trophy className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t("matches")}</span>
              <span className={`h-1.5 w-1.5 rounded-full ${multiMatchEnabled ? "bg-primary animate-pulse-dot" : "bg-muted-foreground/50"}`} />
            </button>
          )}
          {showSwitch && (
            <button
              onClick={toggleMode}
              aria-label="Toggle white-label mode"
              title={mode === "media" ? "Media Mode" : "Betting Mode"}
              className={`hidden md:flex shrink-0 items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-bold transition ${
                mode === "betting"
                  ? "bg-[oklch(0.82_0.24_145)]/20 text-[oklch(0.82_0.24_145)] border border-[oklch(0.82_0.24_145)]/50"
                  : "glass hover:bg-accent"
              }`}
            >
              {mode === "betting" ? <Wallet className="h-3.5 w-3.5" /> : <Newspaper className="h-3.5 w-3.5" />}
              {mode === "media" ? "MEDIA" : "BETTING"}
            </button>
          )}
          {isSuperAdmin && (
            <Link
              to="/admin"
              aria-label="Admin"
              title="Admin"
              onClick={() => void trackEvent("admin_menu_visible", { surface: "header_row2" })}
              className="md:hidden flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-bold glass hover:bg-accent"
            >
              <LayoutDashboard className="h-3.5 w-3.5 text-[oklch(0.78_0.18_70)]" />
              <span>Admin</span>
            </Link>
          )}
          {showPartnerPortal && (
            <Link
              to="/tenant-portal"
              search={partnerPortalSearch}
              aria-label="Partner Portal"
              title="Partner Portal"
              onClick={() =>
                void trackEvent("partner_portal_opened", {
                  surface: "header_row2",
                  impersonating: Boolean(impersonatedTenantId),
                })
              }
              className="md:hidden flex shrink-0 items-center justify-center rounded-lg p-1.5 text-muted-foreground/70 hover:bg-accent hover:text-foreground"
            >
              <Building2 className="h-3.5 w-3.5" />
              <span className="sr-only">Partner Portal</span>
            </Link>
          )}
          {!isAuthenticated && !PREVIEW_MODE && (
            <>
              <Link
                to="/login"
                search={{ from: undefined }}
                aria-label="Login"
                title="Login"
                onClick={() => {
                  void trackEvent("public_login_cta_clicked", { surface: "header_row2" });
                  void trackEvent("auth_navigation_deduplicated", { surface: "header_row2" });
                }}
                className="md:hidden flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-bold glass hover:bg-accent"
              >
                <LogIn className="h-3.5 w-3.5" />
                <span>{t("btn_login")}</span>
              </Link>
              <Link
                to="/registro"
                aria-label="Sign Up Free"
                title="Sign Up Free"
                onClick={() => void trackEvent("public_signup_cta_clicked", { surface: "header_row2" })}
                className="md:hidden flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-bold bg-[oklch(0.58_0.24_27)] text-white shadow hover:bg-[oklch(0.52_0.24_27)]"
              >
                <span>{t("btn_signup_free")}</span>
              </Link>
            </>
          )}
      </div>
      {/* OPERACIÓN VISIBILIDAD — Row 3: Discover hub.
          Horizontal scroller with snap + generous hit targets so every chip
          is reachable on mobile without overlapping siblings. The trailing
          gradient + padding hint that more items live to the right. */}
      <div className="relative -mx-1">
        <div className="flex items-center gap-2.5 overflow-x-auto scrollbar-hide snap-x snap-mandatory px-1 pr-10 py-1 [&>a]:min-h-[40px] [&>a]:snap-start">
          <DiscoverLink to="/nation-wars" icon={<Flag className="h-3.5 w-3.5 text-amber-400" />} label="Nation Wars" />
          <DiscoverLink to="/viral-cards" icon={<Share2 className="h-3.5 w-3.5 text-pink-400" />} label="Viral Cards" />
          <DiscoverLink to="/creator-studio" icon={<Clapperboard className="h-3.5 w-3.5 text-primary" />} label="Creator Studio" />
          <DiscoverLink to="/media-blitz" icon={<Radio className="h-3.5 w-3.5 text-cyan-400" />} label="Media Blitz" />
          <DiscoverLink to="/ambassadors" icon={<Megaphone className="h-3.5 w-3.5 text-[oklch(0.78_0.18_70)]" />} label="Embajadores" />
          <DiscoverLink to="/showcase" icon={<Sparkles className="h-3.5 w-3.5 text-[oklch(0.85_0.18_85)]" />} label="Showcase" />
          <DiscoverLink to="/social" icon={<UsersIcon className="h-3.5 w-3.5 text-[oklch(0.82_0.24_145)]" />} label="Social" />
          <DiscoverLink to="/partners" icon={<Handshake className="h-3.5 w-3.5 text-[oklch(0.78_0.20_50)]" />} label="Partners" />
          {isSuperAdmin && (
            <DiscoverLink to="/admin" icon={<Shield className="h-3.5 w-3.5 text-[oklch(0.70_0.20_270)]" />} label="Admin" />
          )}
        </div>
        <div aria-hidden className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-background via-background/80 to-transparent" />
      </div>
    </div>
  );
}

function DiscoverLink({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <Link
      to={to}
      onClick={() => void trackEvent("admin_menu_visible", { surface: "header_discover", target: to })}
      className="flex shrink-0 items-center gap-1.5 rounded-full glass px-3.5 py-2 text-[12px] font-bold hover:bg-accent active:scale-[0.98] transition"
      aria-label={label}
      title={label}
    >
      {icon}
      <span>{label}</span>
    </Link>
  );
}